import { Text } from "@mantine/core";
import type { ComponentType } from "react";

import {
  AdminActorContext,
  AdminActorContextTypename,
} from "./actor-contexts/AdminActorContext";
import {
  ApiClientActorContext,
  ApiClientActorContextTypename,
} from "./actor-contexts/ApiClientActorContext";
import {
  MarketplaceModuleActorContext,
  MarketplaceModuleActorContextTypename,
} from "./actor-contexts/MarketplaceModuleActorContext";
import {
  PlayerActorContext,
  PlayerActorContextTypename,
} from "./actor-contexts/PlayerActorContext";
import {
  SimulationActorContext,
  SimulationActorContextTypename,
} from "./actor-contexts/SimulationActorContext";
import {
  SystemActorContext,
  SystemActorContextTypename,
} from "./actor-contexts/SystemActorContext";
import {
  WebhookCallbackActorContext,
  WebhookCallbackActorContextTypename,
} from "./actor-contexts/WebhookCallbackActorContext";

/**
 * Discriminator for journal actor contexts.
 *
 * The `JournalActorContextPayload` GraphQL interface is implemented by 7
 * concrete object types, each rendered by a dedicated component. To keep
 * the renderer Open/Closed (registering a new actor type means adding a
 * single map entry — never editing this file's logic), the dispatch is
 * expressed as a `__typename → component` lookup table.
 *
 * Each concrete renderer accepts a fragment-masked input. Because the
 * renderer receives the structural union of every fragment selected on
 * the interface, we narrow at the dispatch boundary to the masked type
 * the concrete component expects. The narrowing happens once, here,
 * rather than being scattered across N branches.
 */
type ActorContextTypename =
  | typeof AdminActorContextTypename
  | typeof PlayerActorContextTypename
  | typeof SystemActorContextTypename
  | typeof MarketplaceModuleActorContextTypename
  | typeof ApiClientActorContextTypename
  | typeof WebhookCallbackActorContextTypename
  | typeof SimulationActorContextTypename;

interface ContextWithTypename {
  __typename: string;
}

// The dispatch table values render the concrete fragment-masked component
// for a `__typename`. We only know the structural union at this layer (the
// renderer receives the merged interface selection from gql.tada), so the
// map is typed against an opaque payload. Each concrete renderer knows
// how to unmask its own fragment via `readFragment`. The `unknown` step
// is the explicit, single boundary where the structural union is resolved
// — exactly equivalent to the per-branch `as unknown as FragmentOf<...>`
// boundary in the previous switch implementation.
type ActorContextComponent = ComponentType<{ data: ContextWithTypename }>;

const asRenderer = (component: ComponentType<{ data: never }>) =>
  component as unknown as ActorContextComponent;

const ACTOR_CONTEXT_RENDERERS: Record<
  ActorContextTypename,
  ActorContextComponent
> = {
  [AdminActorContextTypename]: asRenderer(AdminActorContext),
  [PlayerActorContextTypename]: asRenderer(PlayerActorContext),
  [SystemActorContextTypename]: asRenderer(SystemActorContext),
  [MarketplaceModuleActorContextTypename]: asRenderer(
    MarketplaceModuleActorContext,
  ),
  [ApiClientActorContextTypename]: asRenderer(ApiClientActorContext),
  [WebhookCallbackActorContextTypename]: asRenderer(WebhookCallbackActorContext),
  [SimulationActorContextTypename]: asRenderer(SimulationActorContext),
};

function isKnownActorContext(
  typename: string,
): typename is ActorContextTypename {
  return Object.prototype.hasOwnProperty.call(
    ACTOR_CONTEXT_RENDERERS,
    typename,
  );
}

interface Props {
  context: ContextWithTypename;
}

export function JournalActorContextRenderer({ context }: Props) {
  if (!isKnownActorContext(context.__typename)) {
    return (
      <Text size="xs" c="dimmed">
        {context.__typename}
      </Text>
    );
  }

  const Renderer = ACTOR_CONTEXT_RENDERERS[context.__typename];
  return <Renderer data={context} />;
}
